import * as React from 'react'
import * as ReactDOM from 'react-dom'
import { Modal, ModalProps } from '../components/shared/Modal'

export const useModal = ({
  onCloseCallback,
  isOpen: initialIsOpen = false,
  ...componentProps
}: UseModalOptions): UseModalResult => {
  const [isOpen, setIsOpen] = React.useState(initialIsOpen)

  // NB: body-scroll locking lives in the `Modal` component (`useLockBodyScroll`,
  // which also handles iOS touch-scroll). We deliberately do NOT lock here too:
  // the Modal is what actually renders while open (via `useModal` or directly,
  // e.g. UserTransactions), so one lock there covers every case. Locking in both
  // places previously left the page unscrollable after a modal was dismissed.

  const toggleModal = React.useCallback(() => {
    setIsOpen(() => {
      if (onCloseCallback) onCloseCallback()

      return !isOpen
    })
  }, [isOpen, onCloseCallback])

  const ModalComponent: React.FC<React.PropsWithChildren<ModalProps>> =
    React.useMemo(
      () =>
        ({ children, ...rest }) => {
          const modalRoot = document.getElementById('modal-root')

          if (!modalRoot || !isOpen) return null

          const portalContent = <Modal {...rest}>{children}</Modal>

          return ReactDOM.createPortal(
            portalContent,
            modalRoot
          ) as React.ReactPortal
        },
      [isOpen]
    )

  return {
    isOpen,
    toggleModal,
    Modal: ModalComponent,
    modalProps: {
      toggleModal,
      ...componentProps
    }
  }
}

interface UseModalOptions extends Omit<ModalProps, 'toggleModal'> {
  isOpen?: boolean
  onCloseCallback?: () => void
}

export interface UseModalResult {
  isOpen?: boolean
  toggleModal: () => void
  Modal: React.FC<React.PropsWithChildren<ModalProps>>
  modalProps: ModalProps
}
